import { defineComponent as _defineComponent } from 'vue'
import mapboxgl, {
  MapboxGeoJSONFeature,
  AnyLayer,
  MapMouseEvent,
  EventData,
} from 'mapbox-gl'
import { inject, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useFieldView } from '../FieldViewState'
import { useMap } from '../mapbox/context'


export default /*#__PURE__*/_defineComponent({
  __name: 'LayerGroup',
  props: {
    layers: { default: () => [] },
    filter: null,
    hoverCursor: { default: 'pointer' },
    before: null,
    debug: { type: [Boolean, Array] },
    getDebugContents: null,
    hoverPopup: { type: Boolean },
    hideLayer: { type: Boolean }
  },
  emits: ["click", "hover-on", "hover-off"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    event: 'click',
    data: MapboxGeoJSONFeature[],
    eventData: MapMouseEvent & EventData
  )
  (event: 'hover-on', data: MapboxGeoJSONFeature)
  (event: 'hover-off')
}), expose: any, slots: any, attrs: any }) {

const props = __props as { layers: AnyLayer[], filter?: any[], hoverCursor: string, before?: string, debug?: boolean | string[], getDebugContents?: (feature: mapboxgl.MapboxGeoJSONFeature) => string, hoverPopup?: boolean, hideLayer?: boolean }

const { map, popup } = useMap()
const { setHoveredFeature } = useFieldView()

const source = inject<string>('source')
const hoveredFeature = ref<MapboxGeoJSONFeature>()





const setFilters = (filters?: any[]) => {
  props.layers
    .map(({ id }) => id)
    .forEach((layerId) => {
      map.value.setFilter(layerId, filters)
    })
}

const setBefore = (before?: any) => {
  props.layers.forEach((layer) => {
    map.value.moveLayer(layer.id, before)
  })
}

const setHidden = (hidden?: boolean) => {
  props.layers.forEach((layer) => {
    if (map.value.getLayer(layer.id)) {
      map.value.setLayoutProperty(
        layer.id,
        'visibility',
        hidden === true ? 'none' : 'visible'
      )
    }
  })
}

watch(() => props.filter, setFilters)
watch(() => props.before, setBefore)
watch(() => props.hideLayer, setHidden)

onMounted(() => {
  props.layers.forEach((layer) => {
    const beforeLayerExists = props.before && map.value.getLayer(props.before)
    map.value.addLayer(
      {
        ...layer,
        ...{ source: source as any },
      },
      beforeLayerExists ? props.before : undefined
    )

    if (
      props.debug === true ||
      (Array.isArray(props.debug) && props.debug.includes(layer.id))
    ) {
      configureDebugHover(layer.id)
    }

    if (props.hoverPopup) {
      configureHoverPopup(layer.id)
    }
  })

  if (props.filter) {
    setFilters(props.filter)
  }

  if (props.hideLayer) {
    setHidden(props.hideLayer)
  }

  map.value.on('mousemove', handleMouseMove)
  map.value.on('click', handleClick)
})

onBeforeUnmount(() => {
  props.layers.forEach((layer) => {
    map.value.removeLayer(layer.id)
  })

  map.value.off('mousemove', handleMouseMove)
  map.value.off('click', handleClick)
})

const handleMouseMove = (e: MapMouseEvent & EventData) => {
  const features = map.value.queryRenderedFeatures(e.point)

  if (features.length === 0) {
    map.value.getCanvas().style.cursor = ''
  }

  const feature = features.find((f) => f.source === source)

  if (!feature || !feature.id) {
    if (hoveredFeature.value) {
      clearHover()
    }
    return
  }

  if (hoveredFeature.value && feature.id !== hoveredFeature.value.id) {
    clearHover()
  }

  map.value.getCanvas().style.cursor = props.hoverCursor
  hoveredFeature.value = feature
  map.value.setFeatureState(feature, { hover: true })
  emits('hover-on', feature)
}

const handleClick = (e: MapMouseEvent & EventData) => {
  const features = map.value.queryRenderedFeatures(e.point, {
    layers: props.layers.map((l) => l.id),
  })

  if (features.length) {
    emits('click', features, e)
  }
}

const clearHover = () => {
  if (hoveredFeature.value) {
    map.value.setFeatureState(hoveredFeature.value, { hover: false })
    hoveredFeature.value = undefined
    emits('hover-off')
  }
}

const configureDebugHover = (layerName: string) => {
  map.value.on('mouseenter', layerName, (e) => {
    // Change the cursor style as a UI indicator.
    map.value.getCanvas().style.cursor = 'pointer'
    const item = e.features?.[0]
    if (!item) return

    let html = ''

    if (props.getDebugContents) {
      html = props.getDebugContents(item)
    } else {
      html = `<pre><code>${JSON.stringify(
        { id: item?.id, properties: item?.properties },
        null,
        2
      )}</code></pre>`
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    if (html) {
      popup.value?.setLngLat(e.lngLat).setHTML(html).addTo(map.value)
    }
  })

  map.value.on('mouseleave', layerName, () => {
    map.value.getCanvas().style.cursor = ''
    popup.value?.remove()
  })
}

const configureHoverPopup = (layerName: string) => {
  map.value.on('mouseenter', layerName, (e) => {
    e.preventDefault()
    setHoveredFeature(e.features?.[0] ?? null)
  })

  map.value.on('mouseleave', layerName, (e) => {
    e.preventDefault()
    setHoveredFeature(null)
  })
}

return { __sfc: true,map, popup, setHoveredFeature, source, hoveredFeature, props, emits, setFilters, setBefore, setHidden, handleMouseMove, handleClick, clearHover, configureDebugHover, configureHoverPopup }
}

})